"use client";

import { t } from "@/content/i18n/messages";
import { defaultLocale, getLocalizedPathname, type Locale } from "@/lib/i18n";
import {
  MARKETING_CONSENT_TEXT,
  PRIVACY_NOTICE_PATH,
} from "@/lib/marketing-consent";
import { cn } from "@/lib/utils";

interface MarketingConsentProps {
  /** Unique per form — ties the <label> to the <input>. */
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  locale?: Locale;
  /**
   * Override for forms that keep their own per-locale string tables instead of
   * t(). Defaults to the translated MARKETING_CONSENT_TEXT.
   */
  label?: string;
  /** Override for the "Privacy Notice" link text (same reason as `label`). */
  privacyLabel?: string;
  disabled?: boolean;
  className?: string;
  /** Label text colour override, e.g. for a form sitting on a dark card. */
  labelClassName?: string;
}

/**
 * Optional opt-in for marketing email. Deliberately unticked by default and
 * never required: an enquiry must go through whether or not it is ticked.
 */
export default function MarketingConsent({
  id,
  checked,
  onChange,
  locale = defaultLocale,
  label,
  privacyLabel,
  disabled,
  className,
  labelClassName = "text-neutral-muted",
}: MarketingConsentProps) {
  return (
    <div className={cn("flex items-start gap-3", className)}>
      <input
        id={id}
        name="marketing_consent"
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-border accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed"
      />
      <label
        htmlFor={id}
        className={cn("cursor-pointer text-xs leading-relaxed", labelClassName)}
      >
        {label ?? t(locale, MARKETING_CONSENT_TEXT)}{" "}
        <a
          href={getLocalizedPathname(PRIVACY_NOTICE_PATH, locale)}
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-primary underline underline-offset-2 hover:text-primary-emphasis focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          {privacyLabel ?? t(locale, "Privacy Notice")}
        </a>
      </label>
    </div>
  );
}
